import { DateTime } from "luxon";

import Url from "./url";
import Label from "./label";
import Contributor from "./contributor";
import AffiliateBanner from "./affiliate-banner";
import Asset from "./asset";

const siteReleasedAt = DateTime.fromISO(process.env.GATSBY_RELEASED_AT!);

// Type for imgix image data (matches our GraphQL schema)
export interface imgixData {
  url?: string;
}

// Type for MDX-based pages frontmatter (from PageMdxFrontmatter GraphQL type)
export interface PageMdxFrontmatter {
  id?: string;
  title?: string;
  type?: string;
  url?: any; // UrlYaml type
  breadcrumb?: any[];
  labels?: any[];
  contributors?: any[];
  sideNavigationAdvertisements?: any[];
  relatedArticles?: any[];
  releasedAt?: string;
  createdAt?: string;
  updatedAt: string;
  references?: any[];
  noindex?: boolean;
  stealth?: boolean;
  tableOfContents?: boolean;
  description?: string;
  urlId?: string;
  excludeTitleFromOgImageGeneration?: boolean;
  internalTitle?: string;
  base64Title?: string;
  image?: any;
  sys: any;
}

// Type for MDX-based pages (from PageMdx GraphQL type)
export interface MdxPage {
  id: string;
  frontmatter: PageMdxFrontmatter;
  body?: string;
  excerpt?: string;
}

export default class Page {
  id: string;
  url: Url;
  breadcrumb: Url[];
  title: string;
  image?: any | null;
  description: string;
  releasedAt?: DateTime;
  createdAt?: DateTime;
  updatedAt: DateTime;
  references?: any[];
  labels: Label[];
  content?: any;
  category?: Label;
  metaTitle: string;
  noindex?: boolean;
  excerpt?: string;
  tableOfContents: boolean;
  contributors: Contributor[];
  faq?: any;
  type?: string;
  sideNavigationAdvertisements: AffiliateBanner[];
  excludeTitleFromOgImageGeneration: boolean;
  internalTitle?: string;
  base64Title?: string;

  constructor(page: MdxPage) {
    const pageData = page.frontmatter;

    const labels = pageData.labels?.filter(
      (label: any) =>
        [
          "exploring-a-better-future",
          "culture-and-lifestyle",
          "workstyle",
          "shaping-a-better-future",
        ].indexOf(label!.slug!) == -1
    );

    this.id = page.id;
    this.references = pageData.references || [];
    this.url = new Url(pageData.url || pageData.urlId || page.id);
    this.breadcrumb =
      pageData.breadcrumb?.map((url: any) => new Url(url!)) || [];

    this.title = pageData.title || "";
    this.metaTitle = pageData.title || "";
    this.description = pageData.description || "";

    if (page.frontmatter.image) {
      this.image = new Asset(page.frontmatter.image);
    }

    this.noindex = pageData.noindex || false;
    this.excludeTitleFromOgImageGeneration =
      pageData.excludeTitleFromOgImageGeneration || false;

    if (pageData.internalTitle) {
      this.internalTitle = pageData.internalTitle;
    }

    // Set base64Title from GraphQL resolver or compute it if needed
    this.base64Title = pageData.base64Title || undefined;

    this.updatedAt = DateTime.fromISO(pageData.sys?.updatedAt!).setZone(
      "Asia/Tokyo"
    );

    if (pageData.createdAt) {
      this.createdAt = DateTime.fromISO(pageData.sys?.createdAt!).setZone(
        "Asia/Tokyo"
      );

      if (this.createdAt < siteReleasedAt) {
        this.createdAt = siteReleasedAt;
      }
    }

    if (pageData.releasedAt) {
      this.releasedAt = DateTime.fromISO(pageData.releasedAt!).setZone(
        "Asia/Tokyo"
      );

      if (this.releasedAt < siteReleasedAt) {
        this.releasedAt = siteReleasedAt;
      }

      if (this.updatedAt < this.releasedAt) {
        this.updatedAt = this.releasedAt;
      }
    }

    this.tableOfContents = pageData.tableOfContents || false;

    this.labels = labels?.map((label: any) => new Label(label!)) || [];

    if (labels && labels.length > 0) {
      this.category = new Label(labels[0]!);
    }

    // For MDX pages, use the body content
    if (page.body) {
      this.content = page.body;
    }

    this.excerpt = page.excerpt;

    if (pageData.type) {
      this.type = pageData.type;
    }

    this.contributors =
      pageData.contributors?.map(
        (contributor: any) => new Contributor(contributor!)
      ) || [];

    this.sideNavigationAdvertisements =
      pageData.sideNavigationAdvertisements?.map(
        (banner: any) => new AffiliateBanner(banner!)
      ) || [];
  }

  /**
   * Get image URLs in the legacy format for backward compatibility
   * This creates a compatibility layer for components expecting the old Contentful structure
   */
  get imageUrls() {
    if (!this.image || !this.image.imgix) {
      return null;
    }

    const baseUrl = this.image.imgix.url();

    return {
      cover: {
        publicURL: `${baseUrl}&w=2400&h=1260`,
      },
      thumbnail: {
        publicURL:
          this.type == "Article" || this.type == "Static"
            ? `${baseUrl}?auto=format,compress&fit=crop&crop=left&w=1436&h=1260`
            : `${baseUrl}?auto=format,compress`,
      },
      square: {
        publicURL: `${baseUrl}?&auto=format,compress&fit=crop&fp-x=.08&w=1260&h=1260`,
      },
      ogp: {
        publicURL: this.excludeTitleFromOgImageGeneration
          ? `${baseUrl}?auto=format,compress&w=2400&h=1260`
          : `${baseUrl}?w=2400&h=1260&blend-mode=normal&blend64=${this.base64Title}&blend-x=1436`,
      },
    };
  }
}
